import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  DestroyRef,
  Input,
  signal
} from '@angular/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogRef } from '@angular/cdk/dialog';
import {
  catchError,
  EMPTY,
  finalize,
  switchMap
} from 'rxjs';

import { AuthState } from '@app/modules/users';
import { BrowserApiService } from '@core/common';
import { WINDOW } from '@core/common/tokens';
import { indicateLoading } from '@core/helpers';
import { AuthService } from '@app/modules/users/auth.service';
import { ApiResponse, AuthMethod } from '@app/modules/users/users.entity';
import { HttpErrorFormater } from '@core/http';

import { AUTH_ERRORS, UNKNOWN_REASON } from '../../../modules/users/components/oauth-page/constants';
import { ErrorState, ErrorStateType } from '../../../modules/users/components/oauth-page/interfaces';

@Component({
  selector: 'ui-login-dialog-button[method]',
  templateUrl: './login-dialog-button.component.html',
  styleUrl: './login-dialog-button.component.scss',
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginDialogButtonComponent {
  public readonly dialogRef = inject(DialogRef);
  private readonly browser = inject(BrowserApiService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WINDOW);
  private readonly authState = inject(AuthState);

  @Input()
  public method!: AuthMethod;

  @Input()
  public classList!: string;

  @Input()
  public text?: string;

  @Input()
  public icon!: string;

  @Input()
  public redirectToMain?: boolean = false;

  @Input()
  public disabled = false;

  public readonly isLoading = signal<boolean>(false);
  public readonly errorState = signal<ErrorState>({
    type: null,
    reason: undefined
  });

  redirectToAuth(): void {
    if (this.disabled) {
      return;
    }

    if (this.authState.telegramData()?.isCameFromBot) {
      let hasUncaughtError = true;

      this.authService
        .authenticate('telegram_bot', {
          tgToken: this.authState.telegramData()?.initDataRaw
        })
        .pipe(
          indicateLoading(this.isLoading),
          switchMap((response) => this.authState.setToken(response.token)),
          catchError((error: unknown) => {
            hasUncaughtError = false;

            if (error instanceof HttpErrorResponse) {
              const formattedError = new HttpErrorFormater().getFormattedError(
                error
              );
              const translatedError =
                AUTH_ERRORS[formattedError.format()] || UNKNOWN_REASON;

              this.setError(ErrorStateType.GENERIC_ERROR, translatedError);
            }

            return EMPTY;
          }),
          finalize(() => {
            if (hasUncaughtError) {
              this.setError(ErrorStateType.GENERIC_ERROR, UNKNOWN_REASON);
            }
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          hasUncaughtError = false;
          this.redirect(`auth/oauth`);
        });
    } else {
      this.authService
        .getAuthLink(this.method, false, true)
        .pipe(
          indicateLoading(this.isLoading),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((response: ApiResponse.RedirectUrl) => {
          if (this.redirectToMain) {
            this.authState.saveLastUrl('/');
          } else {
            this.authState.saveLastUrl();
          }

          this.redirect(response.redirect);
        });
    }
  }

  private redirect(url: string): void {
    if (!this.browser.isBrowser()) {
      return;
    }

    this.window!.location.replace(url);
  }

  private setError(type: ErrorStateType, reason?: string): void {
    this.errorState.set({ type, reason });
  }
}
